<script>
// whatsapp-automatic-history-sync.UI.2
// whatsapp-automatic-history-sync.UI.3
// whatsapp-automatic-history-sync.UI.4
// whatsapp-automatic-history-sync.UI.5
// whatsapp-automatic-history-sync.UI.6

// NOTICE: KAPTION-INTEGRATION
// WhatsApp Coexistence History Sync monitoring page
// Shows sync progress for 6-month historical data sync
import KaptionWhatsappRecovery from 'dashboard/components/kaption/KaptionWhatsappRecovery.vue';
import { useAlert } from 'dashboard/composables';
import inboxMixin from 'shared/mixins/inboxMixin';
import SettingsSection from '../../../../../components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import InboxesAPI from '../../../../../api/inboxes';

export default {
  components: {
    KaptionWhatsappRecovery,
    SettingsSection,
    NextButton,
  },
  mixins: [inboxMixin],
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      syncData: null,
      isLoading: true,
      loadError: false,
      isFetching: false,
      isTriggering: false,
      refreshInterval: null,
    };
  },
  computed: {
    displayState() {
      const state = this.syncData?.status;
      if (state === 'completed' && this.syncData?.delivery?.legacy_unverified)
        return 'unverified';
      if (state === 'pending')
        return this.syncData?.automatic_sync?.state || 'unknown';
      return state || 'unknown';
    },
    statusLabel() {
      return this.$t(
        `INBOX_MGMT.WHATSAPP_HISTORY_SYNC.STATUS.${this.displayState.toUpperCase()}`
      );
    },
    statusClass() {
      const classMap = {
        pending: 'bg-n-alpha-2 text-n-slate-12',
        in_progress: 'bg-n-teal-3 text-n-teal-11',
        completed: 'bg-n-green-3 text-n-green-11',
        failed: 'bg-n-ruby-3 text-n-ruby-11',
        declined: 'bg-n-alpha-2 text-n-slate-12',
      };
      return classMap[this.displayState] || 'bg-n-alpha-2 text-n-slate-12';
    },
    isCoexistence() {
      return this.syncData?.is_coexistence || false;
    },
    overallProgress() {
      return (
        this.syncData?.delivery?.provider_progress ??
        this.syncData?.progress?.overall ??
        0
      );
    },
    canTriggerTranslation() {
      return (
        this.syncData?.status === 'completed' &&
        !this.syncData?.translation?.is_complete &&
        this.syncData?.translation?.pending === 0
      );
    },
    hasTranslationInProgress() {
      return (
        this.syncData?.translation?.pending > 0 &&
        this.syncData?.translation?.completed > 0
      );
    },
  },
  mounted() {
    this.fetchSyncStatus();
    // Keep waiting, late media and recoverable read errors observable too.
    this.refreshInterval = setInterval(() => {
      this.fetchSyncStatus();
    }, 10000);
  },
  beforeUnmount() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  },
  methods: {
    async fetchSyncStatus() {
      if (this.isFetching) return;
      this.isFetching = true;
      try {
        const response = await InboxesAPI.getWhatsappHistorySync(this.inbox.id);
        this.syncData = response.data;
        this.loadError = false;
      } catch {
        this.loadError = true;
      } finally {
        this.isLoading = false;
        this.isFetching = false;
      }
    },
    async triggerTranslation() {
      this.isTriggering = true;
      try {
        await InboxesAPI.triggerWhatsappHistoryTranslation(this.inbox.id);
        useAlert(
          this.$t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.TRANSLATION_TRIGGERED')
        );
        this.fetchSyncStatus();
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.TRANSLATION_ERROR'));
      } finally {
        this.isTriggering = false;
      }
    },
    formatNumber(num) {
      return (num || 0).toLocaleString();
    },
    formatDate(dateStr) {
      if (!dateStr) return '-';
      return new Date(dateStr).toLocaleString();
    },
  },
};
</script>

<template>
  <div class="mx-8">
    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <span class="spinner" />
      <span class="ml-2 text-n-slate-11">{{
        $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.LOADING')
      }}</span>
    </div>

    <template v-else>
      <!-- NOTICE: KAPTION-INTEGRATION - Automatic status with a separate administrator recovery flow. -->
      <div
        v-if="loadError"
        role="alert"
        class="p-4 mb-6 rounded-lg bg-n-alpha-2 text-n-slate-12"
      >
        {{ $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.LOAD_ERROR') }}
      </div>
      <div
        v-if="syncData"
        class="p-4 mb-6 rounded-lg bg-n-alpha-2 text-n-slate-12"
        role="status"
      >
        <p>{{ $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.AUTOMATIC') }}</p>
        <p class="mt-2 text-sm text-n-slate-11">
          {{
            $t(
              `INBOX_MGMT.WHATSAPP_HISTORY_SYNC.EXPLANATION.${displayState.toUpperCase()}`
            )
          }}
        </p>
        <p
          v-if="syncData.automatic_sync?.expires_at"
          class="mt-2 text-sm text-n-slate-11"
        >
          {{ $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.WINDOW_END') }}
          {{ formatDate(syncData.automatic_sync.expires_at) }}
        </p>
        <p class="mt-2 text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.LIMITS') }}
        </p>
      </div>
      <!-- Not Coexistence Warning -->
      <div
        v-if="!isCoexistence"
        class="p-4 mb-6 rounded-lg bg-n-alpha-2 border border-n-weak"
      >
        <p class="text-n-slate-12">
          {{ $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.NOT_COEXISTENCE') }}
        </p>
      </div>

      <!-- Sync Status Section -->
      <SettingsSection
        :title="$t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.TITLE')"
        :sub-title="$t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.DESCRIPTION')"
      >
        <div class="space-y-6">
          <!-- Status Badge -->
          <div class="flex items-center gap-3">
            <span class="text-sm font-medium text-n-slate-11">{{
              $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.STATUS_LABEL')
            }}</span>
            <span
              class="px-3 py-1 text-sm font-medium rounded-full"
              :class="statusClass"
            >
              {{ statusLabel }}
            </span>
          </div>

          <!-- Error Message -->
          <div
            v-if="syncData?.error || syncData?.automatic_sync?.error"
            class="p-4 rounded-lg bg-n-ruby-3 border border-n-ruby-7"
          >
            <p class="text-sm text-n-ruby-11">
              <strong>
                {{ $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.ERROR') }}:
              </strong>
              {{ syncData.error || syncData.automatic_sync?.error }}
              <span v-if="syncData.error_code" class="text-n-ruby-11">
                ({{ $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.ERROR_CODE') }}:
                {{ syncData.error_code }})
              </span>
            </p>
          </div>

          <!-- Progress Bar -->
          <div v-if="syncData?.status === 'in_progress'" class="space-y-2">
            <div class="flex justify-between text-sm text-n-slate-11">
              <span>{{
                $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.DELIVERY.PROVIDER_LABEL')
              }}</span>
              <span>{{ overallProgress }}%</span>
            </div>
            <div class="w-full h-2 rounded-full bg-n-slate-3">
              <div
                class="h-2 rounded-full bg-n-teal-9 transition-all duration-300"
                :style="{ width: `${overallProgress}%` }"
              />
            </div>
          </div>

          <!-- Stats Grid -->
          <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
            <!-- Messages Synced -->
            <div class="p-4 rounded-lg bg-n-alpha-2">
              <p class="text-xs text-n-slate-11 uppercase tracking-wide">
                {{ $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.MESSAGES_SYNCED') }}
              </p>
              <p class="text-2xl font-semibold text-n-slate-12 mt-1">
                {{ formatNumber(syncData?.progress?.messages_synced) }}
              </p>
            </div>

            <!-- Conversations Synced -->
            <div class="p-4 rounded-lg bg-n-alpha-2">
              <p class="text-xs text-n-slate-11 uppercase tracking-wide">
                {{
                  $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.CONVERSATIONS_SYNCED')
                }}
              </p>
              <p class="text-2xl font-semibold text-n-slate-12 mt-1">
                {{ formatNumber(syncData?.progress?.conversations_synced) }}
              </p>
            </div>

            <!-- Attachments -->
            <div class="p-4 rounded-lg bg-n-alpha-2">
              <p class="text-xs text-n-slate-11 uppercase tracking-wide">
                {{ $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.ATTACHMENTS') }}
              </p>
              <p class="text-2xl font-semibold text-n-slate-12 mt-1">
                {{ formatNumber(syncData?.attachments?.completed) }}
                <span class="text-sm text-n-slate-11">
                  / {{ formatNumber(syncData?.attachments?.pending) }}
                </span>
              </p>
            </div>

            <!-- Transcriptions -->
            <div class="p-4 rounded-lg bg-n-alpha-2">
              <p class="text-xs text-n-slate-11 uppercase tracking-wide">
                {{ $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.TRANSCRIPTIONS') }}
              </p>
              <p class="text-2xl font-semibold text-n-slate-12 mt-1">
                {{ formatNumber(syncData?.transcription?.completed) }}
                <span class="text-sm text-n-slate-11">
                  / {{ formatNumber(syncData?.transcription?.pending) }}
                </span>
              </p>
            </div>
          </div>

          <p
            v-if="syncData?.attachments?.unavailable"
            class="text-sm text-n-slate-11"
          >
            {{
              $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.UNAVAILABLE', {
                count: syncData.attachments.unavailable,
              })
            }}
          </p>
          <!-- Timestamps -->
          <div class="flex flex-wrap gap-6 text-sm text-n-slate-11">
            <div v-if="syncData?.timestamps?.started_at">
              <span class="font-medium">
                {{ $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.STARTED_AT') }}:
              </span>
              {{ formatDate(syncData.timestamps.started_at) }}
            </div>
            <div v-if="syncData?.timestamps?.completed_at">
              <span class="font-medium">
                {{ $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.COMPLETED_AT') }}:
              </span>
              {{ formatDate(syncData.timestamps.completed_at) }}
            </div>
          </div>
        </div>
      </SettingsSection>

      <div
        v-if="syncData?.delivery"
        class="my-6 text-sm text-n-slate-11 space-y-2"
      >
        <p>
          {{
            $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.DELIVERY.PROVIDER', {
              progress: syncData.delivery.provider_progress,
            })
          }}
        </p>
        <p>
          {{
            $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.DELIVERY.LOCAL', {
              completed: syncData.delivery.imported_deliveries,
              total: syncData.delivery.durable_deliveries,
              pending: syncData.delivery.pending_deliveries,
              failed: syncData.delivery.failed_deliveries,
            })
          }}
        </p>
        <p>{{ $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.DELIVERY.SCOPE') }}</p>
        <p v-if="syncData.delivery.phases.some(phase => phase.observed_gap)">
          {{ $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.DELIVERY.GAP') }}
        </p>
      </div>
      <KaptionWhatsappRecovery
        v-if="syncData?.can_manage_recovery"
        :inbox-id="inbox.id"
        @updated="fetchSyncStatus"
      />
      <!-- Translation Section -->
      <SettingsSection
        v-if="syncData?.status === 'completed'"
        :title="$t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.TRANSLATION_TITLE')"
        :sub-title="
          $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.TRANSLATION_DESCRIPTION')
        "
      >
        <div class="space-y-4">
          <!-- Translation Stats -->
          <div class="grid grid-cols-3 gap-4">
            <div class="p-4 rounded-lg bg-n-alpha-2">
              <p class="text-xs text-n-slate-11 uppercase tracking-wide">
                {{ $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.TRANSLATION_PENDING') }}
              </p>
              <p class="text-2xl font-semibold text-n-slate-12 mt-1">
                {{ formatNumber(syncData?.translation?.pending) }}
              </p>
            </div>
            <div class="p-4 rounded-lg bg-n-alpha-2">
              <p class="text-xs text-n-slate-11 uppercase tracking-wide">
                {{
                  $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.TRANSLATION_COMPLETED')
                }}
              </p>
              <p class="text-2xl font-semibold text-n-green-11 mt-1">
                {{ formatNumber(syncData?.translation?.completed) }}
              </p>
            </div>
            <div class="p-4 rounded-lg bg-n-alpha-2">
              <p class="text-xs text-n-slate-11 uppercase tracking-wide">
                {{ $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.TRANSLATION_FAILED') }}
              </p>
              <p class="text-2xl font-semibold text-n-ruby-11 mt-1">
                {{ formatNumber(syncData?.translation?.failed) }}
              </p>
            </div>
          </div>

          <!-- whatsapp-automatic-history-sync.UI.7 -->
          <div
            v-if="
              syncData?.translation?.is_complete &&
              syncData?.translation?.completed > 0 &&
              syncData?.translation?.failed === 0
            "
            class="p-4 rounded-lg bg-n-green-3 border border-n-green-7"
          >
            <p class="text-n-green-11">
              {{
                $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.TRANSLATION_COMPLETE_MSG')
              }}
            </p>
          </div>

          <!-- Trigger Translation Button -->
          <div v-if="canTriggerTranslation">
            <NextButton :is-loading="isTriggering" @click="triggerTranslation">
              {{
                $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.TRIGGER_TRANSLATION_BTN')
              }}
            </NextButton>
            <p class="mt-2 text-sm text-n-slate-11">
              {{
                $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.TRIGGER_TRANSLATION_HINT')
              }}
            </p>
          </div>

          <!-- Translation In Progress -->
          <div
            v-if="hasTranslationInProgress"
            class="p-4 rounded-lg bg-n-teal-3 border border-n-teal-7"
          >
            <p class="text-n-teal-11">
              {{
                $t('INBOX_MGMT.WHATSAPP_HISTORY_SYNC.TRANSLATION_IN_PROGRESS')
              }}
            </p>
          </div>
        </div>
      </SettingsSection>
    </template>
  </div>
</template>
